import { useParams, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { ConfirmationModalV2 } from 'components/v2/confirmation-modal/confirmation-modal-v2';
import { useVisitState } from 'store/visits/hooks';
import { OwnerParams } from 'shared/types';
import { buildEditVisitPath, buildCancelVisitPath } from 'utils/route-helpers';
import { canCancelVisit, canEditVisit } from 'utils/visit-status-rules';
import { Step } from 'components/visit-form/types';
import { useUiMetadata } from 'store/ui-metadata/hooks';
import { useIsDesktopHost } from 'hooks/use-host-info.hook';
import { useNativeHistoryHeader } from 'hooks/use-native-history-header.hook';
import { VisitDetailsNotFound } from './visit-details-modal-states';

/**
 * A visit in any state, reachable by uuid alone.
 *
 * Previously the only way in was the post-create confirmation route, so every visit arrived wearing a
 * green tick and an offer to cancel it — including the ones already cancelled. The status decides the
 * heading and which actions are still meaningful; the confirmation route keeps its own fixed design.
 */
export const VisitDetailsPageV2 = (): JSX.Element => {
  const { visitUuid } = useParams<{ visitUuid: string }>();
  const ownerParams = useParams<OwnerParams>();
  const navigate = useNavigate();
  const isDesktop = useIsDesktopHost();

  useNativeHistoryHeader(!isDesktop);

  const visitState = useVisitState(visitUuid || '', true);
  const visit = visitState.data;
  const status = visit?.visit_status;

  const metadata = useUiMetadata();
  const editVisitsEnabled = metadata?.ui_metadata.visit_editing_enabled || false;
  const cancelVisitsEnabled = metadata?.ui_metadata.cancel_visits_enabled || false;
  // Both actions build an absolute path, and `generatePath` throws on a missing param rather than
  // returning something harmless. ROOT_PATH supplies both today, so this guards the cast rather than a
  // reachable bug — but withholding the action beats offering one that throws when pressed.
  const hasOwnerParams = Boolean(ownerParams.ownerId && ownerParams.ownerType);

  const handleClose = useCallback(() => {
    // Replace, not push: opening the visit already added an entry, so pushing the list back on top would
    // leave a back gesture reopening the modal it was just used to dismiss.
    navigate('../view', { relative: 'route', replace: true });
  }, [navigate]);

  const handleEditDate = useCallback(() => {
    if (!visitUuid || !ownerParams.ownerId || !ownerParams.ownerType) {
      return;
    }
    navigate(buildEditVisitPath(ownerParams as OwnerParams, visitUuid, Step.Date));
  }, [navigate, visitUuid, ownerParams]);

  const handleCancelVisit = useCallback(() => {
    navigate(buildCancelVisitPath(ownerParams as OwnerParams, visitUuid), {
      state: {
        visitors: visit?.visitors,
        cancelScope: visit?.is_group_visit ? 'group' : 'single',
      },
    });
  }, [navigate, ownerParams, visitUuid, visit?.is_group_visit, visit?.visitors]);

  if (!visitUuid || visitState.error) {
    // Only an outright failure counts as missing. Before the fetch effect runs, `loading` is still false
    // and `data` still null, so treating "no visit" as not-found would flash the error on every deeplink.
    return <VisitDetailsNotFound onClose={handleClose} />;
  }

  // No separate loading branch: the modal owns its own skeleton and holds it inside the same frame while
  // the visit loads. Swapping in a distinct loading modal here would mount a second swipe modal and replay
  // its open animation once the visit arrived — the "double swipe" stutter. One frame, body swaps in place.
  return (
    <ConfirmationModalV2
      visit={visit}
      status={status}
      paneEligible
      onClose={handleClose}
      onEditDate={editVisitsEnabled && hasOwnerParams && canEditVisit(status) ? handleEditDate : undefined}
      onCancelVisit={cancelVisitsEnabled && hasOwnerParams && canCancelVisit(status) ? handleCancelVisit : undefined}
    />
  );
};
